import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSector } from '../../contexts/SectorContext';
import {
  listApprovals,
  approveItem,
  editApproval,
  undoApproval,
  rejectApproval,
  answerApproval,
  fetchAutonomy,
  setAutonomy,
  proposeFollowUps,
  ApprovalDto,
  AutonomyEntry,
  RejectReasonCode,
} from '../services/MendsVisionService';
import { ApprovalCard } from '../components/ApprovalCard';
import { PreferencesSection } from '../components/PreferencesSection';
import {
  ReasonText,
  actionKindLabel,
  answerErrorText,
  approveConflictText,
  explainApproval,
  friendlyApiMessage,
  isOpenStatus,
  reasonText,
  sentence,
} from '../lib/approvalLabels';
import { readApiError, discountEditPayload, discountPercentOf } from '../lib/approvalContent';
import '../styles/mendsAi.css';

type Tab = 'pendentes' | 'historico';
const TABS: { id: Tab; label: string }[] = [
  { id: 'pendentes', label: 'Pendentes' },
  { id: 'historico', label: 'Histórico' },
];
const HISTORY_PAGE = 10;
const TICK_MS = 60_000;

/** Kinds com nível de autonomia editável aqui (mesmo conjunto de antes da reorganização dos rótulos). */
const AUTONOMY_KINDS = new Set([
  'send_message', 'create_follow_up', 'follow_up', 'change_stage', 'update_memory', 'register_notes',
  'refresh_memory', 'generate_briefing', 'open_chat', 'navigate', 'execute_commitment',
]);

const LEVELS = [
  { id: 'sombra', label: 'Sombra' },
  { id: 'aprovar_antes', label: 'Aprovar antes' },
  { id: 'agir_avisar', label: 'Agir e avisar' },
  { id: 'autonoma', label: 'Autônoma' },
];

const byNewest = (a: ApprovalDto, b: ApprovalDto) =>
  (Date.parse(b.createdAt) || 0) - (Date.parse(a.createdAt) || 0) || b.id - a.id;

const ApprovalsPage: React.FC<{ embedded?: boolean }> = ({ embedded }) => {
  const { selectedSectorId } = useSector();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const highlightId = Number(params.get('id') || 0) || null;
  const cardRefs = useRef<Record<number, HTMLElement | null>>({});
  const tabRefs = useRef<Record<Tab, HTMLButtonElement | null>>({ pendentes: null, historico: null });
  const flashRef = useRef<HTMLDivElement | null>(null);
  const scrolledHighlight = useRef<number | null>(null);
  const seededSectorRef = useRef<number | null>(null);
  const [items, setItems] = useState<ApprovalDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>('pendentes');
  const [historyLimit, setHistoryLimit] = useState(HISTORY_PAGE);
  const [editingId, setEditingId] = useState<number | null>(null);
  /** Decisão de desconto: percentual final que o gestor aprova (vai no payload como approvedPercent). */
  const [editPercent, setEditPercent] = useState('');
  const [editBody, setEditBody] = useState('');
  const [busyId, setBusyId] = useState<number | null>(null);
  const [flash, setFlash] = useState<{ ok: boolean; text: string } | null>(null);
  const [blockedById, setBlockedById] = useState<Record<number, ReasonText>>({});
  const [levels, setLevels] = useState<AutonomyEntry[]>([]);
  const [sweeping, setSweeping] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());

  const sectorId = selectedSectorId ? Number(selectedSectorId) : null;

  const load = useCallback(async () => {
    if (!selectedSectorId) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await listApprovals(Number(selectedSectorId));
      setItems(data || []);
      const lv = await fetchAutonomy(Number(selectedSectorId));
      setLevels(lv || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
      setNowMs(Date.now());
    }
  }, [selectedSectorId]);

  useEffect(() => { load(); }, [load]);

  // "Expira em X h" acompanha o relógio sem recarregar a fila.
  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), TICK_MS);
    return () => window.clearInterval(timer);
  }, []);

  // Fila vazia: uma tentativa por montagem do setor. sessionStorage só após sucesso,
  // para a próxima visita poder repetir — sem reentrar neste efeito quando o sweep falha.
  useEffect(() => {
    if (!selectedSectorId || loading || items.length > 0 || sweeping) return;
    if (seededSectorRef.current === selectedSectorId) return;
    const key = `mai-seed-approvals-${selectedSectorId}`;
    try {
      if (sessionStorage.getItem(key) === '1') return;
    } catch { /* ignore */ }
    seededSectorRef.current = selectedSectorId;
    setSweeping(true);
    // Automática ao abrir a tela: orçamento curto (seed), nunca prende o carregamento por 60 s.
    void proposeFollowUps(Number(selectedSectorId), 5, 'seed')
      .then(() => {
        try { sessionStorage.setItem(key, '1'); } catch { /* ignore */ }
        return load();
      })
      .catch(() => { /* ignore — permite retry na próxima visita */ })
      .finally(() => setSweeping(false));
  }, [selectedSectorId, loading, items.length, sweeping, load]);

  const pending = useMemo(() => items.filter((i) => isOpenStatus(i.status)), [items]);
  const history = useMemo(() => items.filter((i) => !isOpenStatus(i.status)).sort(byNewest), [items]);

  useEffect(() => {
    scrolledHighlight.current = null;
  }, [highlightId]);

  // Link com ?id=: abre a aba certa, rola até o cartão e já abre a edição quando ele é editável.
  useEffect(() => {
    if (!highlightId || loading) return;
    if (scrolledHighlight.current === highlightId) return;
    const match = items.find((i) => i.id === highlightId);
    if (!match) return;
    if (!isOpenStatus(match.status)) {
      const index = history.findIndex((i) => i.id === highlightId);
      if (tab !== 'historico') { setTab('historico'); return; }
      if (index >= historyLimit) { setHistoryLimit(index + 1); return; }
    }
    const el = cardRefs.current[highlightId];
    if (!el) return;
    scrolledHighlight.current = highlightId;
    el.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    if (match.status === 'pending' || match.status === 'edited') {
      setEditingId(highlightId);
      setEditBody(match.body || '');
    }
  }, [highlightId, loading, items, history, historyLimit, tab]);

  const clearBlocked = (id: number) =>
    setBlockedById((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });

  /** Começo de uma ação: o aviso anterior (de outro cartão ou aba) deixa de valer. */
  const startAction = (id: number) => {
    setFlash(null);
    setBusyId(id);
  };

  const announce = (ok: boolean, text: string) => {
    setFlash({ ok, text });
    // Depois da ação o foco vai para o aviso: o leitor de tela lê e quem enxerga é levado até ele.
    window.setTimeout(() => flashRef.current?.focus(), 0);
  };

  /** Botão "Propor follow-ups": pedido explícito do vendedor (orçamento manual). */
  const runManualSweep = async () => {
    if (!sectorId) return;
    setSweeping(true);
    try {
      await proposeFollowUps(sectorId, 5, 'manual');
      await load();
    } finally {
      setSweeping(false);
    }
  };

  const onApprove = async (item: ApprovalDto) => {
    if (!sectorId) return;
    startAction(item.id);
    try {
      if (editingId === item.id) {
        const discount = discountEditPayload(item, editPercent);
        await (discount
          ? editApproval(sectorId, item.id, editBody, undefined, discount)
          : editApproval(sectorId, item.id, editBody));
        setEditingId(null);
      }
      const result = await approveItem(sectorId, item.id);
      if (result.executionError) {
        announce(false, friendlyApiMessage(result.executionError, 'A execução falhou — nada foi enviado.'));
      } else {
        clearBlocked(item.id);
        const explained = explainApproval(result);
        if (explained.status.tone === 'blocked' && explained.reason) {
          // Aprovado, mas o disparo foi barrado: nada saiu — nunca anunciar como sucesso.
          announce(false, sentence(explained.reason));
        } else {
          announce(true, explained.result
            ?? (explained.status.label !== 'Aprovado' ? `${explained.status.label}.` : null)
            ?? (result.executed ? 'Feito.' : 'Aprovado.'));
        }
        if (result.navigatePath && (result.actionKind === 'open_chat' || result.actionKind === 'navigate')) {
          navigate(result.navigatePath);
        }
      }
      await load();
    } catch (e: unknown) {
      const err = readApiError(e);
      if (err.status === 409 && (err.code === 'SUPERSEDED' || err.code === 'CONTEXT_UNVERIFIED')) {
        const conflict = approveConflictText(err.code, err.reason);
        if (err.code === 'CONTEXT_UNVERIFIED') setBlockedById((prev) => ({ ...prev, [item.id]: conflict }));
        else clearBlocked(item.id);
        announce(false, sentence(conflict));
      } else {
        announce(false, friendlyApiMessage(err.message, 'Não consegui aprovar agora — nada foi enviado. Tente de novo.'));
      }
      await load();
    } finally {
      setBusyId(null);
    }
  };

  const onUndo = async (id: number) => {
    if (!sectorId) return;
    startAction(id);
    try {
      const result = await undoApproval(sectorId, id);
      const failed = result?.statusReason === 'reversal_failed';
      announce(!failed, failed ? sentence(reasonText('reversal_failed')!) : 'Ação desfeita.');
      await load();
    } catch (e: unknown) {
      announce(false, friendlyApiMessage(readApiError(e).message, 'Não consegui desfazer agora. Atualize a fila e tente de novo.'));
    } finally {
      setBusyId(null);
    }
  };

  const onReject = async (id: number, code?: RejectReasonCode) => {
    if (!sectorId) return;
    startAction(id);
    try {
      if (code) await rejectApproval(sectorId, id, undefined, code);
      else await rejectApproval(sectorId, id);
      if (editingId === id) setEditingId(null);
      clearBlocked(id);
      announce(true, 'Sugestão rejeitada.');
      await load();
    } catch (e: unknown) {
      announce(false, friendlyApiMessage(readApiError(e).message, 'Não consegui rejeitar agora. Tente de novo.'));
    } finally {
      setBusyId(null);
    }
  };

  const onAnswer = async (id: number, answer: string): Promise<string | null> => {
    if (!sectorId) return null;
    setFlash(null);
    try {
      const result = await answerApproval(sectorId, id, answer);
      const status = (result?.status || '').toLowerCase();
      if (status === 'pending' || status === 'edited') {
        announce(true, 'Pronto — a ação está completa e aguarda seu Ok.');
      } else if (status === 'needs_info') {
        announce(true, 'Resposta registrada.');
      } else {
        // Cancelada (info_incomplete, sombra…) ou encerrada: nada vai sair — é um aviso de erro com o motivo.
        const why = reasonText(result?.statusReason) ?? (result ? explainApproval(result).reason : null);
        announce(false, why ? sentence(why) : 'Não consegui completar a ação com essa resposta.');
      }
      await load();
      return null;
    } catch (e: unknown) {
      const err = readApiError(e);
      const text = answerErrorText(err.code, err.message);
      if (err.status === 409) {
        // A pergunta mudou de estado (respondida, expirada, outra resposta): explica e mostra a fila atual.
        announce(false, text);
        await load();
        return null;
      }
      return text;
    }
  };

  const onSaveEdit = async (id: number) => {
    if (!sectorId) return;
    startAction(id);
    try {
      const item = items.find((a) => a.id === id);
      const discount = item ? discountEditPayload(item, editPercent) : undefined;
      await (discount
        ? editApproval(sectorId, id, editBody, undefined, discount)
        : editApproval(sectorId, id, editBody));
      setEditingId(null);
      announce(true, 'Texto salvo. Continua aguardando seu Ok.');
      await load();
    } catch (e: unknown) {
      announce(false, friendlyApiMessage(readApiError(e).message, 'Não consegui salvar o texto. Tente de novo.'));
    } finally {
      setBusyId(null);
    }
  };

  const selectTab = (next: Tab, focus = false) => {
    if (next !== tab) setFlash(null);
    setTab(next);
    if (focus) tabRefs.current[next]?.focus();
  };

  const onTabKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    const index = TABS.findIndex((t) => t.id === tab);
    let next: number | null = null;
    if (e.key === 'ArrowRight') next = (index + 1) % TABS.length;
    else if (e.key === 'ArrowLeft') next = (index - 1 + TABS.length) % TABS.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = TABS.length - 1;
    if (next === null) return;
    e.preventDefault();
    selectTab(TABS[next].id, true);
  };

  const visible = tab === 'pendentes' ? pending : history.slice(0, historyLimit);
  const counts: Record<Tab, number> = { pendentes: pending.length, historico: history.length };

  const sweepButton = sectorId ? (
    <div className="mai-btn-row" style={embedded ? { marginBottom: 16 } : { marginTop: 16 }}>
      <button className="mai-btn mai-btn-soft" disabled={sweeping} onClick={() => void runManualSweep()}>
        {sweeping ? 'Propondo…' : 'Propor follow-ups'}
      </button>
    </div>
  ) : null;

  return (
    <div className={embedded ? 'mai-today-embed' : 'mai-page mai-approvals'}>
      {!embedded ? (
        <header className="mai-page-header">
          <h1 className="mai-title">Aprovações</h1>
          <p className="mai-subtitle">
            Mensagens e follow-ups só saem com o seu Ok. Edite o rascunho se precisar.
          </p>
          {sweepButton}
        </header>
      ) : sweepButton}

      <div
        ref={flashRef}
        role="status"
        aria-live="polite"
        tabIndex={-1}
        className={flash ? `mai-approvals-flash ${flash.ok ? 'mai-cmd-confirm-ok' : 'mai-cmd-confirm-err'}` : 'mai-approvals-flash'}
      >
        {flash?.text}
      </div>

      {!selectedSectorId && <div className="mai-empty">Selecione uma conexão.</div>}

      {selectedSectorId && (
        <>
          <div className="mai-tabs" role="tablist" aria-label="Fila de aprovações">
            {TABS.map((t) => (
              <button
                key={t.id}
                ref={(el) => { tabRefs.current[t.id] = el; }}
                type="button"
                role="tab"
                id={`mai-approvals-tab-${t.id}`}
                aria-selected={tab === t.id}
                aria-controls="mai-approvals-panel"
                tabIndex={tab === t.id ? 0 : -1}
                className={tab === t.id ? 'mai-tab is-active' : 'mai-tab'}
                onClick={() => selectTab(t.id)}
                onKeyDown={onTabKeyDown}
              >
                {t.label}{' '}
                <span className="mai-tab-count">{counts[t.id]}</span>
              </button>
            ))}
          </div>

          <div
            role="tabpanel"
            id="mai-approvals-panel"
            aria-labelledby={`mai-approvals-tab-${tab}`}
          >
            {loading && <div className="mai-empty">Carregando…</div>}
            {!loading && visible.length === 0 && (
              <div className="mai-empty">
                {tab === 'pendentes'
                  ? 'Fila vazia — nada para supervisionar.'
                  : 'Nada no histórico ainda. Aqui aparece o que já foi aprovado, rejeitado ou não enviado.'}
              </div>
            )}

            <div className="mai-approvals-list">
              {visible.map((item) => (
                <ApprovalCard
                  key={item.id}
                  item={item}
                  nowMs={nowMs}
                  highlight={highlightId === item.id}
                  busy={busyId === item.id}
                  editing={editingId === item.id}
                  editBody={editBody}
                  blockedNotice={blockedById[item.id] ?? null}
                  cardRef={(el) => { cardRefs.current[item.id] = el; }}
                  onEditBodyChange={setEditBody}
                  discountPercent={editPercent}
                  onDiscountPercentChange={setEditPercent}
                  onStartEdit={() => {
                    setEditingId(item.id);
                    setEditBody(item.body);
                    setEditPercent(discountPercentOf(item));
                  }}
                  onCancelEdit={() => setEditingId(null)}
                  onSaveEdit={() => void onSaveEdit(item.id)}
                  onApprove={() => void onApprove(item)}
                  onReject={(code) => void onReject(item.id, code)}
                  onUndo={() => void onUndo(item.id)}
                  onAnswer={(answer) => onAnswer(item.id, answer)}
                  onOpenChat={() => navigate(`/chat?contactId=${item.contactId}`)}
                />
              ))}
            </div>

            {tab === 'historico' && history.length > historyLimit && (
              <div className="mai-btn-row">
                <button
                  className="mai-btn mai-btn-ghost"
                  onClick={() => setHistoryLimit((n) => n + HISTORY_PAGE)}
                >
                  Ver mais ({history.length - historyLimit})
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {levels.length > 0 && (
        <section className="mai-section" style={{ marginTop: 36 }}>
          <div className="mai-section-label">Níveis de autonomia</div>
          <p className="mai-card-detail" style={{ marginBottom: 12 }}>
            Sombra não enfileira. Aprovar antes pede Ok. Em Agir e avisar / Autônoma, só ações internas
            (memória, briefing, abrir chat, navegar) podem rodar sozinhas — WhatsApp, follow-up e
            compromisso enfileirado sempre pedem Ok. Retorno explícito não sensível usa o motor de
            compromissos, fora desta fila.
          </p>
          {levels.filter((l) => AUTONOMY_KINDS.has(l.actionKind)).map((l) => (
            <div key={l.actionKind} className="mai-card" style={{ marginBottom: 8 }}>
              <div className="mai-card-row">
                <label className="mai-card-title" style={{ margin: 0 }} htmlFor={`mai-autonomy-${l.actionKind}`}>
                  {actionKindLabel(l.actionKind)}
                </label>
                <select
                  id={`mai-autonomy-${l.actionKind}`}
                  className="mai-select"
                  value={l.level}
                  onChange={async (e) => {
                    if (!sectorId) return;
                    await setAutonomy(sectorId, l.actionKind, e.target.value);
                    await load();
                  }}
                >
                  {LEVELS.map((lv) => (
                    <option key={lv.id} value={lv.id}>{lv.label}</option>
                  ))}
                </select>
              </div>
            </div>
          ))}
        </section>
      )}

      {/* F4: preferências de envio ditas em frase (só em Configurações › Autonomia da IA, não na aba embutida). */}
      {!embedded && sectorId ? <PreferencesSection sectorId={sectorId} /> : null}
    </div>
  );
};

export default ApprovalsPage;
